import React from "react";
import { Card, Space, Alert } from "antd";
import { ApiOutlined, CheckCircleOutlined, DisconnectOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { PicqerConnectionStatus } from "./PicqerConnectionStatus";
import { CHANNELDOCK_REGISTER_URL } from "./ChannelDockRegisterSidebarNotice";

interface WmsConnectionStatusProps {
  details: {
    wms_provider?: "picqer" | "channeldock" | null;
    channeldock_enabled?: boolean;
    channeldock_api_key?: string;
    picqer_enabled?: boolean;
    picqer_fulfilment_customer_id?: number;
  } | null;
}

/** Shows WMS connection status: Picqer or ChannelDock based on wms_provider. */
export const WmsConnectionStatus: React.FC<WmsConnectionStatusProps> = ({ details }) => {
  const { t } = useTranslation();

  if (!details) return null;

  if (details.wms_provider === "channeldock") {
    const isConnected = details.channeldock_enabled && !!details.channeldock_api_key;
    return (
      <Card
        title={
          <Space>
            <ApiOutlined />
            {t("wmsConnection.channeldockTitle", "ChannelDock Integration")}
          </Space>
        }
        size="small"
        style={{ marginTop: 16 }}
      >
        {isConnected ? (
          <Alert
            message={t("wmsConnection.channeldockConnected", "ChannelDock connected")}
            description={t(
              "wmsConnection.channeldockConnectedDescription",
              "Your company is connected to ChannelDock for product and order management."
            )}
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        ) : (
          <Alert
            message={t("wmsConnection.channeldockNotConnected", "ChannelDock not connected")}
            description={
              <>
                {t(
                  "wmsConnection.channeldockNotConnectedDescription",
                  "Create a ChannelDock account, then contact us so we can link it to your dashboard."
                )}{" "}
                <a
                  href={CHANNELDOCK_REGISTER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("channeldockRegister.createAccount", "Create account")}
                </a>
              </>
            }
            type="warning"
            showIcon
            icon={<DisconnectOutlined />}
          />
        )}
      </Card>
    );
  }

  return (
    <PicqerConnectionStatus
      picqerEnabled={details.picqer_enabled}
      picqerFulfilmentCustomerId={details.picqer_fulfilment_customer_id}
    />
  );
};
